class ClfLanding extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;

    this.controller = new AbortController();
    const { signal } = this.controller;
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.hoverQuery = window.matchMedia('(hover: hover)');
    this.stage = this.querySelector('[data-product-stage]');
    this.frames = [...this.querySelectorAll('.clf-frame')];
    this.frameIndex = 0;
    this.stageVisible = true;
    this.frames.forEach((frame, index) => frame.classList.toggle('is-active', index === 0));

    if (this.stage) {
      this.stage.disabled = false;
      this.setFlipped(false);
      this.stage.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse' && this.hoverQuery.matches) this.setFlipped(true);
      }, { signal });
      this.stage.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse') this.setFlipped(false);
      }, { signal });
      this.stage.addEventListener('click', (event) => {
        // Mouse hover already shows the back; keyboard and touch toggle it.
        if (event.detail === 0 || event.pointerType === 'touch' || !this.hoverQuery.matches) {
          this.setFlipped(!this.stage.classList.contains('is-flipped'));
        }
      }, { signal });

      const backImage = this.querySelector('.clf-flip-back img');
      const disableFlip = () => {
        this.setFlipped(false);
        this.stage.disabled = true;
      };
      backImage?.addEventListener('error', disableFlip, { signal });
      if (backImage?.complete && backImage.naturalWidth === 0) disableFlip();

      if ('IntersectionObserver' in window) {
        this.stageObserver = new IntersectionObserver(([entry]) => {
          this.stageVisible = entry.isIntersecting;
          this.updateRotation();
        });
        this.stageObserver.observe(this.stage);
      }
    }

    const videoLaunch = this.querySelector('[data-video-launch]');
    if (videoLaunch && /^[a-zA-Z0-9_-]{11}$/.test(videoLaunch.dataset.videoId || '')) {
      videoLaunch.addEventListener('click', (event) => {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.playVideo(videoLaunch);
      }, { signal });
    }

    this.motionQuery.addEventListener('change', () => this.updateMotion(), { signal });
    document.addEventListener('visibilitychange', () => this.updateRotation(), { signal });
    this.updateMotion();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    window.clearInterval(this.rotationTimer);
    this.stageObserver?.disconnect();
    this.revealObserver?.disconnect();
    this.classList.remove('is-reveal-ready');
  }

  setFlipped(flipped) {
    if (!this.stage || (this.stage.disabled && flipped)) return;
    this.stage.classList.toggle('is-flipped', flipped);
    this.stage.setAttribute('aria-pressed', String(flipped));
    this.stage.setAttribute('aria-label', flipped ? 'Show front of pack' : 'Show back of pack');
  }

  updateMotion() {
    this.animate = this.dataset.motion === 'true' && !this.motionQuery.matches;
    this.revealObserver?.disconnect();
    this.classList.remove('is-reveal-ready');

    // Keep every block visible while merchants select and edit it in Shopify.
    if (this.animate && !window.Shopify?.designMode && 'IntersectionObserver' in window) {
      this.revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          this.revealObserver.unobserve(entry.target);
        });
      }, { threshold: 0.12 });
      this.classList.add('is-reveal-ready');
      this.querySelectorAll('.clf-reveal').forEach((element) => this.revealObserver.observe(element));
    }

    if (!this.animate) {
      this.frameIndex = 0;
      this.frames.forEach((frame, index) => frame.classList.toggle('is-active', index === 0));
    }
    this.updateRotation();
  }

  updateRotation() {
    window.clearInterval(this.rotationTimer);
    if (!this.animate || !this.stageVisible || document.hidden || this.frames.length < 2) return;

    this.rotationTimer = window.setInterval(() => {
      if (this.stage?.classList.contains('is-flipped')) return;
      const nextIndex = (this.frameIndex + 1) % this.frames.length;
      const nextFrame = this.frames[nextIndex];
      if (!nextFrame.complete || nextFrame.naturalWidth === 0) return;
      this.frames[this.frameIndex].classList.remove('is-active');
      nextFrame.classList.add('is-active');
      this.frameIndex = nextIndex;
    }, 2600);
  }

  playVideo(launch) {
    const card = this.querySelector('[data-video-card]');
    if (!card) return;

    const frame = document.createElement('iframe');
    frame.className = 'clf-video-frame';
    frame.src = `https://www.youtube-nocookie.com/embed/${launch.dataset.videoId}?autoplay=1&rel=0&playsinline=1`;
    frame.title = launch.dataset.videoTitle || 'Tofu Cat Litter Australia Plus video';
    frame.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    frame.allowFullscreen = true;
    frame.setAttribute('tabindex', '0');
    card.replaceChildren(frame);

    const fallback = document.createElement('p');
    const link = document.createElement('a');
    fallback.className = 'clf-video-fallback';
    link.href = `https://www.youtube.com/watch?v=${launch.dataset.videoId}`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.textContent = 'Watch on YouTube';
    fallback.appendChild(link);
    card.after(fallback);
    frame.focus();
  }
}

if (!customElements.get('clf-landing')) {
  customElements.define('clf-landing', ClfLanding);
}
